import { immutableSort } from './array.utils';
import { normalizeWhitespace } from './string.utils';

export function normalizeSubjectTerm(raw: string): string {
  return normalizeWhitespace(raw).toLowerCase();
}

// Keep the pattern in sync with `stripSchoolLevelSuffix`.
export function getNameSuffix(schoolLevel: number): string {
  return schoolLevel === 0 ? 'VS' : `${schoolLevel}S`;
}

// Inverse of the suffix `adjustSubjectNaming` appends via `getNameSuffix`: strip a trailing school-level
// marker (" VS" for Vorschule, " <N>S" otherwise) so a level-imported subject like "Deutsch 4S" resolves
// as its base subject "Deutsch". Whitespace between the number/"V" and the "S" is tolerated to also catch
// legacy shapes ("Deutsch 4 S", "Deutsch V S"). The trailing "S" is required on purpose: a bare trailing
// number is NOT a level marker (e.g. "Lernbereich 1" must stay intact). Keep in sync with `getNameSuffix`.
export function stripSchoolLevelSuffix(name: string): string {
  return name.replace(/\s+(?:v|\d+)\s*s$/i, '').trim();
}

export function getAbbreviationSuffix(schoolLevel: number): string {
  return schoolLevel === 0 ? 'V' : `${schoolLevel}`;
}

export function adjustSubjectNaming(
  subject: { name: string; abbreviation: string; schoolLevel: number },
  targetSchoolLevel?: number,
): { name: string; abbreviation: string } {
  if (subject.schoolLevel === targetSchoolLevel) {
    return {
      name: subject.name,
      abbreviation: subject.abbreviation,
    };
  }

  const levelSuffix = getNameSuffix(subject.schoolLevel);
  const abbreviationSuffix = getAbbreviationSuffix(subject.schoolLevel);

  return {
    name: `${subject.name} ${levelSuffix}`,
    abbreviation: `${subject.abbreviation}${abbreviationSuffix}`,
  };
}

/** Ungrouped subjects first, then configured groups and their subjects in saved order. */
export const getOrderedSchoolSubjects = <TSchoolSubject extends { groupId: number | null; order: number }>(
  schoolSubjects: readonly TSchoolSubject[],
  availableSchoolSubjectGroups: readonly { id: number; order: number }[],
): TSchoolSubject[] => {
  const subjectsWithoutGroups = schoolSubjects.filter((s) => s.groupId === null);
  const subjectsWithGroups = schoolSubjects.filter((s) => s.groupId !== null);

  const orderedSubjectsWithoutGroups = immutableSort(subjectsWithoutGroups, (a, b) => a.order - b.order);
  const orderedGroups = immutableSort(availableSchoolSubjectGroups, (a, b) => a.order - b.order);

  const orderedSubjectsWithGroups = orderedGroups.flatMap((group) => {
    const subjectsInGroup = subjectsWithGroups.filter((s) => s.groupId === group.id);
    return immutableSort(subjectsInGroup, (a, b) => a.order - b.order);
  });

  // Subjects and groups load independently. Keep subjects visible while their group is unavailable.
  const knownGroupIds = new Set(availableSchoolSubjectGroups.map((group) => group.id));
  const subjectsWithUnavailableGroups = immutableSort(
    subjectsWithGroups.filter((subject) => !knownGroupIds.has(subject.groupId!)),
    (a, b) => a.groupId! - b.groupId! || a.order - b.order,
  );

  return [...orderedSubjectsWithoutGroups, ...orderedSubjectsWithGroups, ...subjectsWithUnavailableGroups];
};
